<template>
  <div class="create_wrapper add-order">
    <Breadcrumb :items="items" />

    <div class="custom_card">
      <div class="card-header">
        <h4 class="card-title">{{ $t("breadcrumb.productPromotions.add") }}</h4>
      </div>

      <form @submit.prevent="save">
        <div class="container">
          <div class="row">
            <div class="col-12 py-0">
              <p class="section-label">Promotion type</p>
              <div class="promo-types">
                <button
                  type="button"
                  class="promo-type"
                  :class="{ active: form.offer_type === 'same_item_gift' }"
                  @click="form.offer_type = 'same_item_gift'"
                >
                  <strong>Buy 1 Get 1 Free</strong>
                  <span>The second unit is free. No discount percent.</span>
                </button>
                <button
                  type="button"
                  class="promo-type"
                  :class="{ active: form.offer_type === 'same_item_discount' }"
                  @click="form.offer_type = 'same_item_discount'"
                >
                  <strong>Buy 1 Get Discount on Second</strong>
                  <span>The second unit is sold at a percent off.</span>
                </button>
              </div>
            </div>

            <div class="col-lg-12 py-0 mt-3 promo-search">
              <div class="input_wrapper top_label">
                <label class="form-label">{{ $t("forms.labels.product") }}</label>
                <input
                  v-model="productQuery"
                  type="text"
                  class="form-control"
                  placeholder="Search by name, code, or ID"
                  autocomplete="off"
                  @focus="productMenu = true"
                  @input="onProductQuery"
                  @blur="closeProductMenu"
                />
                <ul v-show="productMenu" class="product-menu">
                  <li v-if="needsMoreSearch" class="product-menu__hint">
                    Type a name, code, or ID.
                  </li>
                  <li v-else-if="searching" class="product-menu__hint">{{ $t("table.loadingData") }}</li>
                  <li v-else-if="!products.length" class="product-menu__hint">No products match that search.</li>
                  <template v-else>
                    <li
                      v-for="product in products"
                      :key="product.id"
                      class="product-menu__item"
                      @mousedown.prevent="pickProduct(product)"
                    >
                      <strong>{{ product.name || product.id }}</strong>
                      <span>{{ productMeta(product) }}</span>
                    </li>
                  </template>
                </ul>
              </div>
            </div>

            <div class="col-lg-6 py-0" v-if="form.offer_type === 'same_item_discount'">
              <div class="input_wrapper top_label">
                <input
                  v-model="form.discount_value"
                  type="number"
                  min="0.01"
                  max="99.99"
                  step="0.01"
                  class="form-control"
                  required
                />
                <label class="form-label">{{ $t("forms.labels.discountPercentage") }}</label>
              </div>
              <p class="field-hint">Use more than 0 and less than 100. Two decimal places at most.</p>
            </div>

            <div class="col-lg-6 py-0">
              <div class="input_wrapper top_label">
                <input
                  v-model="form.max_quantity"
                  type="number"
                  min="1"
                  step="1"
                  class="form-control"
                />
                <label class="form-label">Maximum per order</label>
              </div>
              <p class="field-hint">Most units of this product one order can include. Leave empty for no limit.</p>
            </div>

            <div class="col-lg-6 py-0">
              <div class="input_wrapper top_label">
                <input v-model="form.start_at" type="datetime-local" class="form-control" required />
                <label class="form-label">Start</label>
              </div>
            </div>

            <div class="col-lg-6 py-0">
              <div class="input_wrapper top_label">
                <input v-model="form.end_at" type="datetime-local" class="form-control" required />
                <label class="form-label">End</label>
              </div>
              <p class="field-hint" :class="{ 'is-error': dateError }">
                {{ dateError || "The promotion starts at this time and stops at the end time." }}
              </p>
            </div>

          </div>
        </div>
        <div class="col-lg-6 py-0">
              <v-checkbox
                v-model="form.is_active"
                :label="$t('forms.labels.is_active')"
                color="success"
              ></v-checkbox>
        </div>
        <div class="buttons_wrapper">
          <button type="button" class="button_style_2" @click="$router.push('/product-promotions/show-all')">
            {{ $t("cancel") }}
          </button>
          <button type="submit" class="button_style_1" :disabled="saving" :class="{ disabled: saving }">
            {{ $t("forms.submit") }}
            <span class="btn_loader" v-if="saving"></span>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
export default {
  name: "AddProductPromotion",
  data() {
    return {
      saving: false,
      searching: false,
      searchTimer: null,
      productQuery: "",
      productMenu: false,
      products: [],
      selectedProduct: null,
      items: [
        {
          text: this.$t("breadcrumb.mainPage"),
          disabled: false,
          href: "/",
        },
        {
          text: this.$t("breadcrumb.productPromotions.title"),
          disabled: false,
          href: "/product-promotions/show-all",
        },
        {
          text: this.$t("breadcrumb.productPromotions.add"),
          disabled: true,
          href: "",
        },
      ],
      form: {
        base_product_id: "",
        offer_type: "same_item_gift",
        discount_value: "",
        max_quantity: "",
        start_at: "",
        end_at: "",
        is_active: true,
      },
    };
  },
  computed: {
    dateError() {
      if (!this.form.start_at || !this.form.end_at) return "";
      if (this.form.start_at >= this.form.end_at) return "End must be after the start.";
      return "";
    },
    needsMoreSearch() {
      const keyword = (this.productQuery || "").trim();
      return keyword.length < 2 && !/^\d+$/.test(keyword);
    },
  },
  watch: {
    selectedProduct(product) {
      this.form.base_product_id = product ? product.id : "";
    },
    "form.offer_type"(type) {
      if (type !== "same_item_discount") this.form.discount_value = "";
    },
  },
  methods: {
    productLabel(product) {
      if (!product) return "";
      return `${product.name || product.id} ${this.productMeta(product)}`;
    },
    productMeta(product) {
      if (!product) return "";
      return product.code ? `#${product.id} · ${product.code}` : `#${product.id}`;
    },
    onProductQuery() {
      this.productMenu = true;
      if (this.selectedProduct && this.productQuery !== this.productLabel(this.selectedProduct)) {
        this.selectedProduct = null;
      }
      this.onSearch(this.productQuery);
    },
    pickProduct(product) {
      this.selectedProduct = product;
      this.productQuery = this.productLabel(product);
      this.productMenu = false;
    },
    closeProductMenu() {
      setTimeout(() => {
        this.productMenu = false;
      }, 150);
    },
    onSearch(query) {
      clearTimeout(this.searchTimer);
      const keyword = (query || "").trim();
      if (keyword.length < 2 && !/^\d+$/.test(keyword)) {
        this.searching = false;
        this.products = this.selectedProduct ? [this.selectedProduct] : [];
        return;
      }
      this.searchTimer = setTimeout(() => this.searchProducts(keyword), 300);
    },
    searchProducts(keyword = "") {
      this.searching = true;
      this.$axios
        .get("products", { params: { keyword, per_page: 20 } })
        .then((res) => {
          const payload = res.data.data;
          const rows = payload.data || payload || [];
          this.products = Array.isArray(rows) ? rows : [];
          if (this.selectedProduct && !this.products.some((row) => row.id === this.selectedProduct.id)) {
            this.products = [this.selectedProduct, ...this.products];
          }
        })
        .catch((err) => this.notifyError(err))
        .finally(() => {
          this.searching = false;
        });
    },
    save() {
      if (!this.form.base_product_id) {
        this.notifyError({ response: { data: { message: this.$t("forms.validation.product") } } });
        return;
      }
      if (this.dateError) {
        this.notifyError({ response: { data: { message: this.dateError } } });
        return;
      }
      this.saving = true;
      const payload = {
        base_product_id: this.form.base_product_id,
        offer_type: this.form.offer_type,
        start_at: this.form.start_at.replace("T", " "),
        end_at: this.form.end_at.replace("T", " "),
        is_active: this.form.is_active,
        max_quantity: this.form.max_quantity ? Number(this.form.max_quantity) : null,
      };
      if (this.form.offer_type === "same_item_discount") {
        payload.discount_value = this.form.discount_value;
      }
      this.$axios
        .post("product-promotions", payload)
        .then((res) => {
          this.$iziToast.success({
            timeout: 2000,
            message: res?.data?.message || this.$t("save"),
            position: "bottomRight",
          });
          this.$router.push("/product-promotions/show-all");
        })
        .catch((err) => this.notifyError(err))
        .finally(() => {
          this.saving = false;
        });
    },
    notifyError(err) {
      this.$iziToast.error({
        timeout: 3000,
        message: err?.response?.data?.message || this.$t("table.noData"),
        position: "bottomRight",
      });
    },
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
  },
};
</script>

<style lang="scss" scoped>
.promo-search {
  position: relative;
  z-index: 8;

  .form-control {
    width: 100%;
  }
}

.product-menu {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 30;
  margin: 0;
  padding: 6px 0;
  list-style: none;
  max-height: 280px;
  overflow: auto;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.14);
}

.product-menu__hint,
.product-menu__item {
  padding: 8px 14px;
}

.product-menu__hint {
  color: #6b747c;
  font-size: 13px;
}

.product-menu__item {
  display: flex;
  flex-direction: column;
  line-height: 1.35;
  cursor: pointer;

  span {
    font-size: 12px;
    opacity: 0.75;
  }

  &:hover {
    background: #eef2ff;
  }
}

.section-label {
  margin: 8px 0 10px;
  font-weight: 600;
}

.promo-types {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.promo-type {
  text-align: start;
  border: 1px solid var(--mainColor-2);
  border-radius: 10px;
  background: transparent;
  color: inherit;
  padding: 14px 16px;
  cursor: pointer;

  strong {
    display: block;
    margin-bottom: 4px;
  }

  span {
    display: block;
    font-size: 13px;
    line-height: 1.4;
    opacity: 0.8;
  }

  &.active {
    background: var(--mainColor-2);
    color: #fff;

    span {
      opacity: 0.95;
    }
  }
}

.field-hint {
  margin: -12px 8px 8px;
  font-size: 13px;
  opacity: 0.75;

  &.is-error {
    color: #c62828;
    opacity: 1;
  }
}

.buttons_wrapper {
  gap: 12px;
}

@media (max-width: 768px) {
  .promo-types {
    grid-template-columns: 1fr;
  }
}
</style>
